import { useCallback, useEffect, useMemo, useState } from 'react';
import { API } from '~/constants';
import { corpUserStore } from '~/models';
import { getApi, post } from '~/utils';

interface SuiteManagePermission {
    suite_id: number;
    can_manually_edit: boolean;
}

const useSuiteManagePermission = () => {
    const [suiteManagePermissions, setSuiteManagePermissions] = useState<SuiteManagePermission[]>(
        []
    );
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!corpUserStore.hasSysAdmin) {
            setLoading(false);
            return;
        }
        post<SuiteManagePermission[]>(
            getApi(API.suite.get_manage_permission, corpUserStore.tenantId)
        )
            .then((res) => setSuiteManagePermissions(res.data || []))
            .catch(() => setSuiteManagePermissions([]))
            .finally(() => setLoading(false));
    }, []);

    const permissionMap = useMemo(() => {
        const permissionMap: Record<number, SuiteManagePermission> = {};
        suiteManagePermissions.forEach((permission) => {
            permissionMap[permission.suite_id] = permission;
        });
        return permissionMap;
    }, [suiteManagePermissions]);

    const canManuallyEdit = useCallback(
        (suiteId?: number) => {
            return suiteId !== undefined && !!permissionMap[suiteId]?.can_manually_edit;
        },
        [permissionMap]
    );

    const isPermissionValid = useCallback(
        (suiteId?: number) => {
            return suiteId !== undefined && !loading && !!permissionMap[suiteId];
        },
        [loading, permissionMap]
    );

    return { canManuallyEdit, isPermissionValid, loading };
};

export default useSuiteManagePermission;
